import { Injectable } from '@angular/core';
import { action, transaction } from '@datorama/akita';
import { AppState } from '@models/AppState';

/* import { stat } from 'fs';*/
import { QuoteSessionQuery } from '../session/session.query';

import { ApplicationStore } from './application.store';

@Injectable({ providedIn: 'root' })
export class ApplicationService {
	constructor(
		private readonly store: ApplicationStore,
		private readonly session: QuoteSessionQuery,
	) { }

	/**
	 * Will show or hide the loader, and allow modification of the loader based on the values provided
	 *
	 * @param loading a boolean value representing whether or not the loader should be shown
	 * @param largeLoaderText the text displayed to the user in the loader
	 * @param largeLoaderDelay whether or not the loader should persist for a certain period of time
	 * @param largeLoaderLock the name of the component the loader should be locked to.
	 * Will require `this.constructor.name` to be passed in order to remove loading if it was previously
	 * locked by that component
	 */
	@action('Set Loading')
	@transaction()
	public setLoading(
		loading: boolean,
		largeLoaderText: string = 'Fetching...',
		largeLoaderDelay: number = 0,
		largeLoaderLock: string = '',
	): ApplicationService {
		this.store.update(state => {
			if (state.largeLoaderLock && largeLoaderLock !== state.largeLoaderLock) return state;
			if (loading === state.loading) return state;

			return {
				...state,
				loading,
				largeLoaderText,
				largeLoaderDelay,
				largeLoaderLock: !loading && !!largeLoaderLock ? undefined : largeLoaderLock,
			};
		});

		return this;
	}

	@action('Set Active Pet')
	public setActivePet(petId: string): void {
		this.store.update(state => ({
			...state,
			activePet: petId,
		}));
	}

	@action('Set Last Pet Active')
	public setLastPetActive(): void {
		const pets = this.session.getValue().pets;
		const { petId } = pets.length > 0 ? pets[pets.length - 1] : pets[0];
		this.store.update(state => ({
			...state,
			activePet: petId,
		}));
	}

	@action('Set SSO')
	public setSsoState(ssoState: string): void {
		this.store.update(state => ({
			...state,
			ssoState,
		}));
	}

	@action('Set USAA SSO Inactive')
	public setUsaaSsoInactive(usaaSsoInactive: boolean): void {
		this.store.update(state => ({
			...state,
			usaaSsoInactive,
		}));
	}

	@action('Set USAA Login Attempt')
	public setUsaaAttemptedLogin(usaaAttemptedLogin: boolean): void {
		this.store.update(state => ({
			...state,
			usaaAttemptedLogin,
		}));
	}

	@action('Update Full App State')
	public update(appState: Partial<AppState>): void {
		this.store.update(state => ({
			...state,
			...appState,
		}));
	}

	@action('Set No Kameleoon Flag')
	public setKameleoonEnabled(kameleoonEnabled: boolean): void {
		this.store.update(state => ({
			...state,
			kameleoonEnabled
		}));
	}
}
